import type { Asset, Folder } from './types';

const isAssetInSubfolderRegex = /\//;
const isFolderRegex = /\/$/;

const DOCUMENT_SIZE_LIMIT = 10 * 1024 * 1024;
const IMAGE_SIZE_LIMIT = 10 * 1024 * 1024;
const VIDEO_SIZE_LIMIT = 10 * 1024 * 1024;

export const mimeTypes = {
  documents: [
    'application/pdf',
    'application/zip',
    'application/x-zip-compressed',
    'multipart/x-zip',
    'text/csv',
    'application/csv',
  ],
  images: [
    'image/avif',
    'image/gif',
    'image/jpeg',
    'image/png',
    'image/svg+xml',
  ],
  videos: [
    'audio/wave',
    'audio/wav',
    'audio/x-wav',
    'audio/x-pn-wav',
    'audio/webm',
    'video/webm',
    'audio/ogg',
    'video/ogg',
    'application/ogg',
    'video/mp4',
    'video/quicktime',
  ]
};

// Trims path from asset name and removes assets in subfolders
export const filterAssetData = (data: { assets: Asset[], folders: Folder[] }, path: string) => {
  let assets = data && data.assets && data.assets.filter(a => !isFolderRegex.test(a.Name)) || [];

  let folders = data && data.folders || [];

  const pathRemoveAsset = (obj: Asset): Asset => ({
    ...obj,
    Name: obj.Name.replace(path, '')
  });
  const pathRemoveFolder = (obj: Folder): Folder => ({
    ...obj,
    Name: obj.Name.replace(path, '')
  });

  // Only show assets in selected folder
  assets = assets.map<Asset>(pathRemoveAsset).filter(a => (!isAssetInSubfolderRegex.test(a.Name)));
  folders = folders.map<Folder>(pathRemoveFolder);

  return {
    assets,
    folders
  };
};

// An uploaded JPG/PNG is saved as "abc_convert.png", and a few seconds later the resizer replaces it
// with a compressed "abc-default.jpg" and deletes it (see uploadFiles). Its address stops working then.
const CONVERTING = /_convert\.(png|jpe?g)$/i;
// One still there after this long wasn't converted and won't be: it's an ordinary file from then on.
const CONVERT_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * True while an uploaded image is waiting to be replaced by its compressed copy. `uploading`: an upload
 * on this page is still waiting for its conversions, so its files count whatever this computer's clock says.
 */
export const isConverting = (
  asset: { Name: string; LastModified?: string | null },
  { uploading = false, now = Date.now() }: { uploading?: boolean; now?: number } = {}
): boolean => {
  if (!CONVERTING.test(asset.Name)) return false;
  if (uploading) return true;
  // Millisecond strings from the API, or ISO dates.
  const n = Number(asset.LastModified);
  const at = Number.isNaN(n) ? Date.parse(asset.LastModified || '') : n;
  return !at || now - at < CONVERT_TIMEOUT_MS;
};

/** "abc_convert.png" -> "abc-default.jpg", the name of the compressed copy. */
export const convertedName = (name: string): string => name.replace(CONVERTING, '-default.jpg');

export interface CustomFile {
  file: File;
  isBiggerThanSizeLimit: boolean;
}

/** The name a file is stored under: every character except letters, digits, '.', '-' and '_' becomes '-'. */
export const sanitizeFileName = (name: string): string => name.replace(/[^A-Za-z0-9._-]/g, '-');

export const sanitizeFilesData = (files: File[] = [], path = ''): CustomFile[] => files.map<CustomFile>((file) => {
  const newName = sanitizeFileName(file.name);
  const newFile = new File([file], `${path}${newName}`, { type: file.type });

  return {
    file: newFile,
    isBiggerThanSizeLimit: false
  }
}).filter((wrapper) => {
  // Filter out all ineligible files
  // - Must be an acceptable mime type (documents/images/videos)
  return mimeTypes.documents.includes(wrapper.file.type) ||
    mimeTypes.images.includes(wrapper.file.type) ||
    mimeTypes.videos.includes(wrapper.file.type);
}).map((wrapper) => {
  let { isBiggerThanSizeLimit } = wrapper;

  if (mimeTypes.documents.includes(wrapper.file.type)) {
    isBiggerThanSizeLimit = wrapper.file.size > DOCUMENT_SIZE_LIMIT;
  } else if (mimeTypes.images.includes(wrapper.file.type)) {
    isBiggerThanSizeLimit = wrapper.file.size > IMAGE_SIZE_LIMIT;
  } else if (mimeTypes.videos.includes(wrapper.file.type)) {
    isBiggerThanSizeLimit = wrapper.file.size > VIDEO_SIZE_LIMIT;
  }

  return {
    ...wrapper,
    isBiggerThanSizeLimit
  };
});

export const optimisticFilesData = (files: CustomFile[]) => files.map<Asset>(({ file }) => ({
  __typename: 'Asset',
  Key: `${file.name}`,
  Name: `${file.name}`,
  LastModified: `${file.lastModified}`,
  Size: file.size,
}));
